// @ts-check
// ====== Cache layer client-side (stale-while-revalidate) ======
// Riduce i fetch ripetuti su Supabase. Pattern:
//   1. Risposta cached -> servita immediata (stale)
//   2. In background fetch fresh
//   3. Quando arriva la fresh, aggiorna cache + callback `onUpdate`

const _cache = new Map(); // key -> { data, ts, inflight }
const DEFAULT_TTL = 60_000; // 1 minuto: oltre questo, refetch in background
const HARD_TTL = 5 * 60_000; // 5 minuti: oltre questo, niente stale, fetch sincrono

/**
 * @param {string} key Chiave univoca della cache (es. 'ruoli-attivi', 'health')
 * @param {() => Promise<any>} fetcher Funzione che ritorna i dati freschi
 * @param {Object} [opts]
 * @param {number} [opts.ttl] ms dopo cui considerare stale (default 60s)
 * @param {number} [opts.hardTtl] ms dopo cui non servire piu' stale (default 5min)
 * @param {(data: any) => void} [opts.onUpdate] Callback chiamato quando arriva la fresh
 */
async function cachedFetch(key, fetcher, opts = {}) {
  const ttl = opts.ttl || DEFAULT_TTL;
  const hardTtl = opts.hardTtl || HARD_TTL;
  const now = Date.now();
  const entry = _cache.get(key);

  // Cache fresh (entro TTL) -> ritorna direttamente
  if (entry && now - entry.ts < ttl) {
    return entry.data;
  }

  // Cache stale ma entro hardTtl -> stale-while-revalidate
  if (entry && now - entry.ts < hardTtl) {
    // Background refresh (non await)
    if (!entry.inflight) {
      entry.inflight = fetcher()
        .then((fresh) => {
          entry.data = fresh;
          entry.ts = Date.now();
          entry.inflight = null;
          if (opts.onUpdate) opts.onUpdate(fresh);
          return fresh;
        })
        .catch((err) => {
          entry.inflight = null;
          console.warn('[cache] background fetch failed', key, err);
        });
    }
    return entry.data;
  }

  // Cache mancante o troppo vecchia -> fetch sincrono
  const fresh = await fetcher();
  _cache.set(key, { data: fresh, ts: now, inflight: null });
  return fresh;
}

function invalidateCache(key) {
  if (key) _cache.delete(key);
  else _cache.clear();
}

function getCachedKeys() {
  return [..._cache.keys()];
}

Object.assign(globalThis, { cachedFetch, invalidateCache, getCachedKeys });
export {};
